import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface Version {
  version: string;
  date: string;
  type: 'major' | 'minor' | 'patch';
  highlights: string[];
  sections: {
    added?: string[];
    fixed?: string[];
    changed?: string[];
    removed?: string[];
  };
}

export default function Changelog() {
  const navigate = useNavigate();
  const [selectedVersion, setSelectedVersion] = useState<string>('1.3.0');

  const versions: Version[] = [
    {
      version: '1.3.0',
      date: '2025-01-24',
      type: 'minor',
      highlights: [
        'Enhanced Developer Mode with colorful UI',
        'Component file path tracking',
        'Matrix rain animations'
      ],
      sections: {
        added: [
          '🎨 Colorful gradient-based developer panel design',
          '📍 File path tooltips for all components on hover',
          '🌈 Rainbow gradient titles and neon glow effects',
          '✨ Matrix rain animation in dev mode',
          '🎯 Component hierarchy tracking with file locations',
          '💫 Animated status badges with gradient backgrounds',
          '📊 Visual activity bars in dev panel footer',
          '🔥 Enhanced dev mode for CloudFlare Functions pages',
          'Component labeling with data-component attributes',
          'Drag and drop file upload functionality'
        ],
        changed: [
          'Dev panel upgraded from basic black to vibrant purple/blue gradients',
          'Component status indicators now use animated gradient badges',
          'Dev mode borders enhanced with glow animations',
          'Improved dev panel layout with better spacing'
        ],
        fixed: [
          'File upload display not showing in Shared Files area',
          'Back to Share Options button resetting app state',
          'Upload More Files button not working properly',
          'Drag and drop file upload not functioning',
          'Done button in share modal not full width',
          'Component labels not visible in Receive mode'
        ]
      }
    },
    {
      version: '1.2.0',
      date: '2025-01-24',
      type: 'minor',
      highlights: [
        'Admin dashboard with authentication',
        'System statistics and analytics',
        'Navigation improvements'
      ],
      sections: {
        added: [
          'Complete admin dashboard with authentication system',
          'Admin login page with animated gradient background',
          'Protected admin routes with session management',
          'System statistics dashboard with real-time data',
          'Admin stats API endpoint (/api/admin/stats)',
          'Interactive task flow visualization',
          'Upload trends chart (7-day visualization)',
          'Storage distribution by file type',
          'Recent activity timeline',
          'Quick action buttons for admin tasks'
        ],
        changed: [
          'Removed public dashboard link from main navigation',
          'Dashboard route now redirects to home page',
          'Improved navigation structure for better UX',
          'Enhanced README with admin dashboard documentation'
        ],
        fixed: [
          'Navigation confusion with empty dashboard page',
          'Proper routing for authenticated vs public users'
        ]
      }
    },
    {
      version: '1.1.0',
      date: '2025-01-23',
      type: 'minor',
      highlights: [
        'Fixed file upload display issues',
        'Improved documentation',
        'Added changelog'
      ],
      sections: {
        added: [
          'Comprehensive documentation in README.md',
          'Interactive changelog route at /changelog',
          'Console logging for debugging',
          'Vite build system with hot module replacement',
          'TypeScript support for better type safety',
          'Tailwind CSS for consistent styling'
        ],
        fixed: [
          'File upload display issue in Shared Files area',
          'Race condition when switching to manage view',
          'API endpoint URL format for listing files',
          'Component loading lifecycle with useEffect hooks'
        ],
        changed: [
          'Migrated from vanilla JS to React + TypeScript',
          'Restructured project to use src/ directory',
          'Improved build process with Vite',
          'Updated all component imports to new structure'
        ]
      }
    },
    {
      version: '1.0.1',
      date: '2025-01-22',
      type: 'patch',
      highlights: [
        'Domain migration to fastfiles.app'
      ],
      sections: {
        changed: [
          'Updated domain from fastfile.link to fastfiles.app',
          'Improved domain configuration across all services'
        ],
        fixed: [
          'Domain references in all components',
          'API endpoint URLs to use new domain'
        ]
      }
    },
    {
      version: '1.0.0',
      date: '2025-01-21',
      type: 'major',
      highlights: [
        'Initial release of FastFiles',
        'Send and Receive modes',
        'No authentication required'
      ],
      sections: {
        added: [
          'Send and Receive file modes',
          'No authentication required for basic usage',
          'Magic link authentication for managing receivers',
          'CloudFlare infrastructure (Workers, Pages, R2, D1)',
          'Interactive architecture visualization',
          'Mobile-optimized responsive design',
          'Share via URL, Email, SMS, WhatsApp, QR code',
          'File upload with drag and drop',
          'Image thumbnail previews',
          'Dashboard for managing receivers',
          'Instant link generation with random prefixes',
          'Secure file storage in R2',
          'Metadata tracking in D1 database',
          'Real-time file listing',
          'Download individual files',
          'Persistent receiver management'
        ]
      }
    }
  ];

  const roadmap = [
    { feature: 'Bulk download as ZIP', status: 'planned' },
    { feature: 'File expiration settings', status: 'planned' },
    { feature: 'Password protection', status: 'planned' },
    { feature: 'Email notifications', status: 'planned' },
    { feature: 'Upload progress indicators', status: 'in-progress' },
    { feature: 'File type restrictions', status: 'planned' },
    { feature: 'Mobile app', status: 'considering' },
    { feature: 'Analytics dashboard', status: 'considering' },
    { feature: 'Custom branding', status: 'considering' },
    { feature: 'API for integrations', status: 'planned' }
  ];

  const getVersionBadgeColor = (type: string) => {
    switch(type) {
      case 'major': return 'bg-red-500';
      case 'minor': return 'bg-blue-500';
      case 'patch': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'in-progress': return 'text-blue-600 bg-blue-50';
      case 'planned': return 'text-green-600 bg-green-50';
      case 'considering': return 'text-gray-600 bg-gray-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/')}
            className="text-gray-600 hover:text-gray-800 flex items-center gap-2"
          >
            ← Back to FastFiles
          </button>
          <div className="text-sm text-gray-500">
            Current Version: <span className="font-mono font-semibold text-blue-600">v{versions[0].version}</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            FastFiles Changelog
          </h1>
          <p className="text-xl text-gray-600">
            Track our journey and see what's new
          </p>
        </div>

        {/* Version Timeline */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="md:col-span-1">
            <div className="sticky top-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Version History</h2>
              <div className="space-y-3">
                {versions.map((v) => (
                  <button
                    key={v.version}
                    onClick={() => setSelectedVersion(v.version)}
                    className={`w-full text-left p-4 rounded-lg border transition-all ${
                      selectedVersion === v.version
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <span className="font-mono font-semibold text-lg">v{v.version}</span>
                      <span className={`px-2 py-1 text-xs text-white rounded-full ${getVersionBadgeColor(v.type)}`}>
                        {v.type}
                      </span>
                    </div>
                    <div className="text-sm text-gray-500 mb-2">{v.date}</div>
                    <div className="space-y-1">
                      {v.highlights.map((h, i) => (
                        <div key={i} className="text-xs text-gray-600">• {h}</div>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="md:col-span-2">
            {versions.filter(v => v.version === selectedVersion).map((version) => (
              <div key={version.version} className="bg-white rounded-2xl shadow-lg p-8">
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-3xl font-bold text-gray-900">Version {version.version}</h2>
                    <span className={`px-3 py-1 text-sm text-white rounded-full ${getVersionBadgeColor(version.type)}`}>
                      {version.type} release
                    </span>
                  </div>
                  <p className="text-gray-600">Released on {version.date}</p>
                </div>

                {version.sections.added && version.sections.added.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-green-600 mb-3 flex items-center gap-2">
                      <span className="text-2xl">✨</span> Added
                    </h3>
                    <ul className="space-y-2">
                      {version.sections.added.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-700">
                          <span className="text-green-500 mt-1">+</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {version.sections.fixed && version.sections.fixed.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-blue-600 mb-3 flex items-center gap-2">
                      <span className="text-2xl">🔧</span> Fixed
                    </h3>
                    <ul className="space-y-2">
                      {version.sections.fixed.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-700">
                          <span className="text-blue-500 mt-1">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {version.sections.changed && version.sections.changed.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-orange-600 mb-3 flex items-center gap-2">
                      <span className="text-2xl">🔄</span> Changed
                    </h3>
                    <ul className="space-y-2">
                      {version.sections.changed.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-700">
                          <span className="text-orange-500 mt-1">~</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {version.sections.removed && version.sections.removed.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-xl font-semibold text-red-600 mb-3 flex items-center gap-2">
                      <span className="text-2xl">🗑️</span> Removed
                    </h3>
                    <ul className="space-y-2">
                      {version.sections.removed.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-700">
                          <span className="text-red-500 mt-1">-</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Roadmap Section */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Roadmap</h2>
          <p className="text-gray-600 mb-8">
            Here's what we're working on and considering for future releases
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            {roadmap.map((item, i) => (
              <div key={i} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:shadow-sm transition-shadow">
                <span className="text-gray-800">{item.feature}</span>
                <span className={`px-3 py-1 text-xs font-medium rounded-full ${getStatusColor(item.status)}`}>
                  {item.status.replace('-', ' ')}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 text-gray-500">
          <p>Have a feature request? <a href="mailto:feedback@fastfiles.app" className="text-blue-600 hover:underline">Send us feedback!</a></p>
          <p className="mt-2">Visit <a href="/" className="text-blue-600 hover:underline">FastFiles Home</a></p>
        </div>
      </div>
    </div>
  );
}